<template>
  <div class="site-header-container">
    <header class="site-header">
      <div class="site-header__container container">
        <!-- Left -->
        <div class="site-header__left">
          <button
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <icon-menu />
          </button>

          <button
            class="site-header__search-button icon-button"
            type="button"
            @click="
              openOverlay({
                component: 'predictive-search',
                ignoreDismissed: true,
                namespace: 'predictiveSearch',
              })
            "
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.search')"
            />

            <icon-search />
          </button>
        </div>

        <!-- Logo -->
        <component :is="titleElement" class="site-header__center">
          <a class="site-header__logo" :href="$variable('routes.root')">
            <span
              class="visually-hidden"
              v-text="$variable('store.shopName')"
            />

            <brand-logo />
          </a>
        </component>

        <!-- Right -->
        <div class="site-header__right">
          <search-bar
            :is-search-template="$variable('page.type') === 'search'"
            @click="handleSearch"
          />

          <transition name="fade">
            <button
              v-if="getActiveOverlay"
              class="site-header__custom-overlay window-overlay"
              tabindex="-1"
              type="button"
              aria-hidden="true"
              @click.prevent="handleCloseOverlay"
            />
          </transition>

          <button
            id="market-selector-desktop"
            class="site-header__country-switcher icon-button"
            type="button"
            @click.prevent="handleMarketOverlay"
          >
            <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
            <img
              class="market-selector__flag"
              :src="`https://cdn.shopify.com/static/images/flags/${market.toLowerCase()}.svg`"
              :alt="market || $variable('store.market')"
            />

            <span
              class="text-desktop-paragraph-xs-bold"
              v-text="market || $variable('store.country')"
            />

            <span class="site-header__dropdown-trigger">
              <icon-chevron-down />
            </span>
          </button>

          <button class="site-header__accessibility icon-button" type="button">
            <span id="eyeAble_customToolOpenerID">
              <icon-accessibility />
            </span>
          </button>

          <a
            class="site-header__account icon-button"
            :href="$variable('routes.account.url')"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.account')"
            />

            <icon-account />
          </a>

          <div class="site-header__wishlist wishlist-header-link">
            <a class="wkh-button" href>
              <span class="wkh-icon">
                <span
                  class="visually-hidden"
                  v-text="$string('general.site_header.wishlist')"
                />

                <icon-wishlist />
              </span>

              <span
                class="site-header__wishlist-badge wkh-counter text-mobile-paragraph-xs-bold"
                :class="$class('hidden')"
              />
            </a>
          </div>

          <a
            class="site-header__cart icon-button"
            :href="$variable('routes.cart.url')"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />

            <icon-cart />

            <span
              class="site-header__cart-badge critical-clear text-mobile-paragraph-xs-bold"
              :class="{ [$class('hidden')]: !cart.item_count }"
              aria-hidden="true"
              v-text="cart.item_count"
            />
          </a>
        </div>
      </div>
    </header>

    <!-- Predictive search -->
    <teleport to="body">
      <overlay
        key="predictiveSearch"
        block-class="predictive-search-overlay"
        direction="right-down"
        namespace="predictiveSearch"
        :show-close="false"
        type="drawer-shelf"
      >
        <template #body>
          <predictive-search
            v-if="getComponentVisible('predictive-search')"
            :links="searchInitialSuggestions"
          />

          <div
            v-if="!getComponentActive('predictive-search')"
            class="predictive-search critical-component-clear"
          >
            <div class="predictive-search__header">
              <button class="predictive-search__close" type="button">
                <icon-arrow-left />
              </button>

              <form class="search-bar">
                <label
                  class="visually-hidden"
                  for="loading-header-search"
                  v-text="$string('general.site_header.search_placeholder')"
                />

                <input
                  id="loading-header-search"
                  class="search-bar__input"
                  type="search"
                />
              </form>
            </div>

            <div class="predictive-search__results">
              <div
                class="predictive-search__suggestions"
                :class="[$class('loading')]"
              >
                <p
                  class="predictive-search__title text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
                  v-text="$string('general.predictive_search.suggestions')"
                />

                <ul>
                  <li
                    v-for="(_, index) of [false, false, false, false]"
                    :key="`loading-menu-${index}`"
                    class="predictive-search__list"
                  >
                    <span class="predictive-search__suggestion" />
                  </li>
                </ul>
              </div>

              <div
                class="predictive-search__products"
                :class="[$class('loading')]"
              >
                <div class="predictive-search__title-wrapper">
                  <p
                    class="predictive-search__title text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
                    v-text="
                      $string('general.predictive_search.recently_viewed')
                    "
                  />
                </div>

                <ul>
                  <li
                    v-for="(_, index) of [false, false, false]"
                    :key="`loading-product-${index}`"
                    class="predictive-search__list"
                  >
                    <div class="predictive-search__item">
                      <div class="predictive-search__image-wrapper" />

                      <div class="predictive-search__info">
                        <p
                          class="predictive-search__title text-mobile-paragraph-m-regular text-desktop-paragraph-s-regular"
                          v-html="$string('accessibility.loading')"
                        />

                        <span class="predictive-search__prices" />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {String} searchInitialSuggestions - Initial search bar suggestions.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
import { defineAsyncComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useComponentsStore from '~stores/components';
import useCartStore from '~stores/cart';

import Overlay from '~global/overlay/overlay';
import SearchBar from '~async/search-bar/search-bar';

import BrandLogo from '~icons/general/brand-logo.svg';
import IconAccessibility from '~icons/general/accessibility.svg';
import IconAccount from '~icons/general/account.svg';
import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconCart from '~icons/general/cart.svg';
import IconMenu from '~icons/general/menu.svg';
import IconSearch from '~icons/general/search.svg';
import IconWishlist from '~icons/general/wishlist.svg';
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    IconAccessibility,
    IconAccount,
    IconArrowLeft,
    IconCart,
    IconMenu,
    IconSearch,
    IconWishlist,
    IconChevronDown,
    MenuDrawer: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.menu-drawer' */
          '~async/menu-drawer/menu-drawer'
        ),
    }),
    Overlay,
    PredictiveSearch: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.predictive-search' */
          '~async/predictive-search/predictive-search'
        ),
    }),
    SearchBar,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    searchInitialSuggestions: {
      type: String,
      default: '',
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value);
      },
    },
  },

  data() {
    return {
      market: this.$variable('store.country'),
    };
  },

  computed: {
    ...mapState(useComponentsStore, {
      getComponentExists: 'getExists',
    }),

    ...mapState(useOverlaysStore, ['getActiveOverlay']),
    ...mapState(useCartStore, { cart: 'response' }),
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
      toggleOverlay: 'toggle',
    }),

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      const navigationLayer = document.querySelector('.main-navigation.liquid');

      if (navigationLayer) {
        navigationLayer.classList.toggle('main-navigation--open');

        document.querySelector('body').classList.add('no-scroll');

        /**
         * Tracking mobile menu teaser impressions.
         */
        const teaser = document.querySelectorAll(
          '.main-navigation__teaser-mobile-container > .main-navigation__dropdown-teaser',
        );
        if (teaser.length > 0) {
          for (const tea of teaser) {
            const teaserUrl = tea.getAttribute('href')
              ? tea.getAttribute('href')
              : '';
            const teaserTitle = tea.getAttribute('data-track')
              ? tea.getAttribute('data-track')
              : '';
            cnvs.EventBus.emit(`dataLayer:view_promotion`, {
              event: 'view_promotion',
              promotion: {
                promotion_id: teaserUrl,
                promotion_name: teaserTitle,
                creative_slot: 'header-navigation',
                creative_name: 'Mobile Menu Teaser',
              },
            });
          }
        }
      }
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return;
      }

      event.preventDefault();

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      });
    },

    /**
     * Handle predictive search close.
     */
    handleCloseOverlay() {
      this.closeOverlay();
    },

    handleMarketOverlay() {
      this.openOverlay({
        component: 'market-selector',
        ignoreDismissed: true,
        namespace: 'marketSelector',
      });
    },

    /**
     * Handle search click.
     */
    handleSearch() {
      if (cnvs.page.type === 'search') {
        cnvs.EventBus.emit('search:open');
        return;
      }

      this.openOverlay({
        component: 'predictive-search',
        ignoreDismissed: true,
        namespace: 'predictiveSearch',
        focusElements: {
          elements: [document.querySelector('#header-search')],
          prepend: true,
        },
      });
    },
  },
};
</script>
